import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Router, useMatch } from '@gatsbyjs/reach-router';
import { navigate } from 'gatsby';

import ErrorBoundary from '../common/ErrorBoundary';
import { TipJarTool } from '../common/TipJar';
import { ShareTool } from '../common/Share';
import LEOContactForm from './LEOContactForm';

function LEORouteDispatch({ description, successPage, baseUrl }) {
  const isFirstRender = useRef(true);
  const stateMatch = useMatch(`${baseUrl}:state/`);
  const regionMatch = useMatch(`${baseUrl}:state/:region/`);
  // eslint-disable-next-line no-nested-ternary
  const urlState = regionMatch
    ? regionMatch.state
    : stateMatch
      ? stateMatch.state
      : '';
  const urlRegion = regionMatch ? regionMatch.region : '';

  // Use URL values as initial values in case a state/region specific URL was used
  const [state, setState] = useState(urlState);
  const [region, setRegion] = useState(urlRegion);

  // Navigate if there are changes to `state`
  useEffect(() => {
    // On the first render, allow a preset `region` to stay defined.
    // On all subsequent renders, setting the state should also reset `region`.
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setRegion('');
    }

    if (state) {
      navigate(`${baseUrl}${state}/`);
    }
    // Only trigger this hook on changes to `state`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Navigate if there are changes to `region`
  useEffect(() => {
    if (region) {
      navigate(`${baseUrl}${state}/${region}/`);
    } else if (state) {
      navigate(`${baseUrl}${state}/`);
    }
    // Only trigger this hook on changes to `region`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  // Render the child
  return (
    <>
      <h1>Local election office directory</h1>

      <ul className='-ml-5'>
        <li>
          Contact your local election office if you have any questions about
          voting in your area.
        </li>
        <li>
          You can hand-deliver your voter registration form or your absentee
          ballot request form to your local election office.
        </li>
        <li>
          You can also return your absentee ballot in person to your local
          election office in any state except Mississippi and Tennessee.
        </li>
      </ul>

      <LEOContactForm
        description={description}
        successPage={successPage}
        state={state}
        region={region}
        onSelectState={setState}
        // Ensure `region` is a string
        onSelectRegion={(newRegion) =>
          newRegion ? setRegion(newRegion.toString()) : setRegion('')
        }
        baseUrl={baseUrl}
      />
    </>
  );
}

LEORouteDispatch.propTypes = {
  description: PropTypes.string,
  successPage: PropTypes.string,
  baseUrl: PropTypes.string,
};

export default function LEOPage(props) {
  const { description, isEmbed } = props;
  const baseUrl = isEmbed ? '/embed/leo/' : '/local-election-offices/';
  const childProps = { ...props, baseUrl };

  return (
    <ErrorBoundary>
      {/* min height is to reserve enough height to show the combobox dropdown */}
      <div className='min-h-[500px]' data-element-id='tool_leo'>
        <Router>
          <TipJarTool
            path={`${baseUrl}donate/`}
            tool='leo'
            noThanksPath={`${baseUrl}share/`}
          />
          <ShareTool
            path={`${baseUrl}share/`}
            description={description}
            sharePath={`${baseUrl}`}
          />
          <LEORouteDispatch {...childProps} path={`${baseUrl}*`} />
        </Router>
      </div>
    </ErrorBoundary>
  );
}

LEOPage.propTypes = {
  description: PropTypes.string,
  initialText: PropTypes.string,
  successPage: PropTypes.string,
  isEmbed: PropTypes.bool,
};
